import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { ActionSet } from 'models/action-set';
import { Action } from 'models/action';

type BrowseActionsProps = {
    actionSet: ActionSet;
    onActionSelected?: (action: Action, categoryId: string, subCategoryId: string) => void;
    onActionCleared?: () => void;
    onTouched?: () => void;
};
const BrowseActions: FC<BrowseActionsProps> = ({ actionSet, onActionSelected, onActionCleared, onTouched }) => {
    const { t: translate } = useTranslation(['orders', 'client-directory']);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
    const [selectedAction, setSelectedAction] = useState<string | null>(null);

    const categoryOptions = () => {
        return (actionSet.categories || []).map((category) => ({
            label: category.name,
            id: category._id,
        }));
    };

    const subCategoryOptions = () => {
        if (!selectedCategory) return [];
        const category = actionSet.categories.find((cat) => cat._id === selectedCategory);
        return category
            ? category.subCategories.map((subCategory) => ({
                  label: subCategory.name,
                  id: subCategory._id,
              }))
            : [];
    };

    const actionOptions = () => {
        if (!selectedSubCategory) return [];
        const actions = actionSet.actions.filter(
            (action) =>
                action.mainCategory._id === selectedCategory &&
                Array.isArray(action.subCategory) &&
                action.subCategory.some((sub) => sub._id === selectedSubCategory),
        );
        return actions.map((action) => ({
            label: `${action.number} - ${action.name}`,
            id: action._id,
        }));
    };

    const clearSelectedAction = () => {
        setSelectedAction(null);
        if (onActionCleared) {
            onActionCleared();
        }
    };

    const handleCategorySelected = (category: string) => {
        setSelectedCategory(category);
        setSelectedSubCategory(null);
        clearSelectedAction();
    };

    const handleSubCategorySelected = (subCategory: string) => {
        setSelectedSubCategory(subCategory);
        clearSelectedAction();
    };

    const handleActionSelectChanged = (action: string) => {
        setSelectedAction(action);
        if (onActionSelected) {
            const selectedAction = actionSet.actions.find((a) => a._id === action);
            if (selectedAction) {
                onActionSelected(selectedAction, selectedCategory || '', selectedSubCategory || '');
            }
        }
    };

    return (
        <>
            <Box mb={2}>
                <FormControl fullWidth onFocus={onTouched}>
                    <InputLabel id="category-label">{translate('orders:CATEGORY')}</InputLabel>
                    <Select
                        labelId="category-label"
                        value={selectedCategory ?? ''}
                        label={translate('orders:CATEGORY')}
                        onChange={(event) => handleCategorySelected(event.target.value as string)}
                    >
                        {categoryOptions().map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <Box mb={2}>
                <FormControl fullWidth disabled={selectedCategory === null}>
                    <InputLabel id="sub-category-label">{translate('client-directory:SUB_CATEGORY')}</InputLabel>
                    <Select
                        labelId="sub-category-label"
                        value={selectedSubCategory ?? ''}
                        label={translate('client-directory:SUB_CATEGORY')}
                        onChange={(event) => handleSubCategorySelected(event.target.value as string)}
                    >
                        {subCategoryOptions().map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <Box mb={2}>
                <FormControl fullWidth disabled={selectedSubCategory === null}>
                    <InputLabel id="action-label">{translate('orders:ACTION')}</InputLabel>
                    <Select
                        labelId="action-label"
                        value={selectedAction ?? ''}
                        label={translate('orders:ACTION')}
                        onChange={(event) => handleActionSelectChanged(event.target.value as string)}
                    >
                        {actionOptions().map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </>
    );
};

export default BrowseActions;
